/**
 * Device-local cart (BR-7). No login; persisted to localStorage; survives opening WhatsApp.
 * Lines keep a display snapshot so the cart renders instantly/offline; the order log only sends
 * ids + quantities and the server re-reads prices (D-24). Each line keeps its barcode (the
 * variant's, else the product's — D-44) for the WhatsApp message; `undefined` = not known yet
 * (added from a card, filled from the product detail in the background).
 */
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { MAX_CART_LINE_QTY } from '@/lib/pricing'
import { persistStorage } from '@/lib/storage'
import type { CartValidateLine, Fulfilment } from '@/types/api'

export interface CartShop {
  id: number
  slug: string
  name: string
  /** Town name. */
  town: string
  whatsappNumber: string
  latitude: number | null
  longitude: number | null
}

export interface CartLine {
  productId: number
  variantId: number | null
  quantity: number
  slug: string
  name: string
  nameMl: string
  brand: string
  /** Thumbnail URL. */
  image: string | null
  price: number | null
  mrp: number | null
  variantType: string
  variantLabel: string
  barcode?: string | null
  shop: CartShop
}

export type NewCartLine = Omit<CartLine, 'quantity'>

interface CartState {
  lines: CartLine[]
  /** Per-shop choice; absent = default (BR-6). */
  fulfilment: Record<number, Fulfilment>
  add: (line: NewCartLine, quantity?: number) => void
  increment: (productId: number, variantId: number | null) => void
  decrement: (productId: number, variantId: number | null) => void
  remove: (productId: number, variantId: number | null) => void
  setFulfilment: (shopId: number, f: Fulfilment) => void
  /** Fill barcodes from a product detail: variant id → barcode, plus the product's own. */
  setBarcodes: (
    productId: number,
    product: string | null,
    variants: { id: number; barcode: string | null }[],
  ) => void
  clearShop: (shopId: number) => void
  /** Apply POST /cart/validate: today's names, prices and barcodes; drop lines no longer sold. */
  refresh: (updates: CartValidateLine[]) => number
}

const same = (l: CartLine, productId: number, variantId: number | null) =>
  l.productId === productId && l.variantId === variantId

const clampAdd = (q: number) => Math.min(Math.max(Math.trunc(q), 1), MAX_CART_LINE_QTY)

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      lines: [],
      fulfilment: {},
      add: (line, quantity = 1) =>
        set((s) => {
          const qty = clampAdd(quantity)
          const existing = s.lines.find((l) => same(l, line.productId, line.variantId))
          if (existing) {
            return {
              lines: s.lines.map((l) =>
                l === existing
                  ? {
                      ...l,
                      quantity: Math.min(l.quantity + qty, MAX_CART_LINE_QTY), // BR-7 per line
                      barcode: line.barcode ?? l.barcode,
                    }
                  : l,
              ),
            }
          }
          return { lines: [...s.lines, { ...line, quantity: qty }] }
        }),
      increment: (productId, variantId) =>
        set((s) => ({
          lines: s.lines.map((l) =>
            same(l, productId, variantId)
              ? { ...l, quantity: Math.min(l.quantity + 1, MAX_CART_LINE_QTY) }
              : l,
          ),
        })),
      decrement: (productId, variantId) =>
        set((s) => ({
          lines: s.lines
            .map((l) => (same(l, productId, variantId) ? { ...l, quantity: l.quantity - 1 } : l))
            .filter((l) => l.quantity >= 1),
        })),
      remove: (productId, variantId) =>
        set((s) => ({ lines: s.lines.filter((l) => !same(l, productId, variantId)) })),
      setFulfilment: (shopId, f) => set((s) => ({ fulfilment: { ...s.fulfilment, [shopId]: f } })),
      setBarcodes: (productId, product, variants) =>
        set((s) => ({
          lines: s.lines.map((l) => {
            if (l.productId !== productId) return l
            const v = variants.find((x) => x.id === l.variantId)
            return { ...l, barcode: (v ? v.barcode : product) ?? null }
          }),
        })),
      clearShop: (shopId) => set((s) => ({ lines: s.lines.filter((l) => l.shop.id !== shopId) })),
      refresh: (updates) => {
        let removed = 0
        set((s) => ({
          lines: s.lines.flatMap((l) => {
            const u = updates.find((x) => same(l, x.product_id, x.variant_id ?? null))
            if (!u) return [l]
            if (!u.available) {
              removed += 1
              return []
            }
            return [
              {
                ...l,
                name: u.name ?? l.name,
                price: u.price === undefined ? l.price : u.price,
                mrp: u.mrp === undefined ? l.mrp : u.mrp,
                variantLabel: u.variant_label ?? l.variantLabel,
                barcode: u.barcode === undefined ? l.barcode : u.barcode,
              },
            ]
          }),
        }))
        return removed
      },
    }),
    {
      name: 'angadi.cart',
      storage: createJSONStorage(() => persistStorage),
      version: 2,
      // v1 lines lack the shop coordinates and barcode; the app wasn't live, so start fresh.
      migrate: () => ({ lines: [], fulfilment: {} }),
    },
  ),
)
